(() => {
  'use strict';

  const DAY_IN_MS = 24 * 60 * 60 * 1000;

  const SPECIES_DATA = {
    dog: {
      name: 'Dog',
      eventName: 'Whelping',
      eventNameLower: 'whelping',
      offspringSingular: 'puppy',
      offspringPlural: 'puppies',
      gestationDays: 63,
      methods: [
        {
          value: 'mating',
          label: 'Mating / breeding date',
          estimatedDays: 63,
          minimumDays: 58,
          maximumDays: 68,
          conceptionOffset: 0,
          hint:
            'Convenient but less precise. Whelping may occur 58–68 days after a single mating because sperm can survive for several days.'
        },
        {
          value: 'ovulation',
          label: 'Ovulation date — progesterone timed',
          estimatedDays: 63,
          minimumDays: 62,
          maximumDays: 64,
          conceptionOffset: 0,
          hint:
            'The most accurate method. Whelping usually occurs about 63 days after ovulation.'
        },
        {
          value: 'lh-surge',
          label: 'LH surge date',
          estimatedDays: 65,
          minimumDays: 64,
          maximumDays: 66,
          conceptionOffset: 2,
          hint:
            'Whelping usually occurs about 65 days after the LH surge. Ovulation generally follows approximately two days later.'
        },
        {
          value: 'diestrus',
          label: 'First day of diestrus — vaginal cytology',
          estimatedDays: 57,
          minimumDays: 56,
          maximumDays: 58,
          conceptionOffset: -6,
          hint:
            'Whelping generally occurs about 57 days after the first day of cytological diestrus.'
        }
      ],
      milestones: [
        {
          startDay: 16,
          endDay: 18,
          title: 'Implantation',
          description:
            'Embryos implant. Avoid unnecessary medications and keep nutrition steady.'
        },
        {
          startDay: 25,
          endDay: 30,
          title: 'Ultrasound confirmation',
          description:
            'This is a useful window to confirm pregnancy and assess fetal heartbeats.'
        },
        {
          startDay: 45,
          endDay: 45,
          title: 'Fetal skeletons begin to mineralise',
          description:
            'Radiographs may begin to show fetal skeletons. A clearer puppy count is usually possible from approximately day 55.'
        },
        {
          startDay: 55,
          endDay: 55,
          title: 'Set up the whelping box',
          description:
            'Introduce a quiet, warm and easy-to-clean whelping area before labour begins.'
        },
        {
          startDay: 58,
          endDay: 58,
          title: 'Begin close monitoring',
          description:
            'Watch appetite, behaviour and temperature. A temperature drop may precede labour, but it is not perfectly reliable.'
        }
      ]
    },

    cat: {
      name: 'Cat',
      eventName: 'Queening',
      eventNameLower: 'queening',
      offspringSingular: 'kitten',
      offspringPlural: 'kittens',
      gestationDays: 65,
      methods: [
        {
          value: 'mating',
          label: 'Mating / breeding date',
          estimatedDays: 65,
          minimumDays: 63,
          maximumDays: 67,
          conceptionOffset: 0,
          hint:
            'Cats are induced ovulators, so a known mating date is a useful reference. Normal gestation is commonly around 63–67 days.'
        }
      ],
      milestones: [
        {
          startDay: 16,
          endDay: 21,
          title: 'Nipple changes may appear',
          description:
            'The nipples may become pinker and more prominent during the third week.'
        },
        {
          startDay: 21,
          endDay: 28,
          title: 'Ultrasound confirmation',
          description:
            'Pregnancy and fetal heartbeats can often be confirmed by ultrasound.'
        },
        {
          startDay: 40,
          endDay: 45,
          title: 'Radiographic kitten count',
          description:
            'Fetal skeletons begin to mineralise, allowing radiographs to help estimate litter size.'
        },
        {
          startDay: 55,
          endDay: 55,
          title: 'Prepare a queening area',
          description:
            'Provide a warm, private nesting box in a quiet, low-traffic location.'
        }
      ]
    },

    rabbit: {
      name: 'Rabbit',
      eventName: 'Kindling',
      eventNameLower: 'kindling',
      offspringSingular: 'kit',
      offspringPlural: 'kits',
      gestationDays: 31,
      methods: [
        {
          value: 'mating',
          label: 'Mating date',
          estimatedDays: 31,
          minimumDays: 30,
          maximumDays: 33,
          conceptionOffset: 0,
          hint:
            'Rabbits are induced ovulators, so the mating date is usually a useful reference. Gestation commonly lasts around 30–33 days.'
        }
      ],
      milestones: [
        {
          startDay: 10,
          endDay: 14,
          title: 'Pregnancy may be palpable',
          description:
            'An experienced veterinarian may be able to confirm pregnancy by gentle abdominal palpation.'
        },
        {
          startDay: 21,
          endDay: 21,
          title: 'Minimise stress and handling',
          description:
            'Keep the environment quiet and maintain good-quality forage and water.'
        },
        {
          startDay: 28,
          endDay: 28,
          title: 'Add the nest box',
          description:
            'Provide a clean nest box with suitable bedding. Fur pulling may occur shortly before kindling.'
        }
      ]
    },

    guineapig: {
      name: 'Guinea pig',
      eventName: 'Birth',
      eventNameLower: 'birth',
      offspringSingular: 'pup',
      offspringPlural: 'pups',
      gestationDays: 68,
      methods: [
        {
          value: 'mating',
          label: 'Pairing / mating date',
          estimatedDays: 68,
          minimumDays: 59,
          maximumDays: 72,
          conceptionOffset: 0,
          hint:
            'Guinea-pig gestation commonly lasts 59–72 days, with larger litters often arriving earlier.'
        }
      ],
      milestones: [
        {
          startDay: 15,
          endDay: 21,
          title: 'Pregnancy assessment',
          description:
            'A veterinarian may detect pregnancy by careful examination or ultrasound.'
        },
        {
          startDay: 30,
          endDay: 30,
          title: 'Review nutrition',
          description:
            'Maintain unrestricted hay, appropriate pellets and reliable dietary vitamin C.'
        },
        {
          startDay: 49,
          endDay: 49,
          title: 'Monitor closely for illness',
          description:
            'Late pregnancy carries a risk of pregnancy toxaemia. Seek urgent help if the sow reduces food intake or appears unwell.'
        },
        {
          startDay: 63,
          endDay: 63,
          title: 'Prepare for birth',
          description:
            'Keep the enclosure calm and clean. Guinea pigs do not usually construct a nest.'
        }
      ]
    },

    ferret: {
      name: 'Ferret',
      eventName: 'Birth',
      eventNameLower: 'birth',
      offspringSingular: 'kit',
      offspringPlural: 'kits',
      gestationDays: 42,
      methods: [
        {
          value: 'mating',
          label: 'Mating date',
          estimatedDays: 42,
          minimumDays: 41,
          maximumDays: 44,
          conceptionOffset: 0,
          hint:
            'Ferret gestation is usually tightly grouped at approximately 41–44 days after mating.'
        }
      ],
      milestones: [
        {
          startDay: 12,
          endDay: 14,
          title: 'Pregnancy assessment',
          description:
            'An experienced veterinarian may be able to confirm pregnancy by gentle palpation.'
        },
        {
          startDay: 28,
          endDay: 28,
          title: 'Increase support and prepare a nest',
          description:
            'Provide a warm, dark and quiet nesting area and ensure adequate nutrition.'
        },
        {
          startDay: 35,
          endDay: 35,
          title: 'Final preparation',
          description:
            'Kits are born hairless, blind and deaf, so a warm, draught-free nest is essential.'
        }
      ]
    },

    horse: {
      name: 'Horse',
      eventName: 'Foaling',
      eventNameLower: 'foaling',
      offspringSingular: 'foal',
      offspringPlural: 'foals',
      gestationDays: 340,
      methods: [
        {
          value: 'covering',
          label: 'Last covering / insemination date',
          estimatedDays: 340,
          minimumDays: 320,
          maximumDays: 360,
          conceptionOffset: 0,
          hint:
            'Mares average approximately 340 days, but healthy pregnancies may vary considerably around this figure.'
        }
      ],
      milestones: [
        {
          startDay: 14,
          endDay: 16,
          title: 'Early ultrasound and twin check',
          description:
            'Confirm pregnancy and check for twins during the important early management window.'
        },
        {
          startDay: 25,
          endDay: 30,
          title: 'Fetal heartbeat assessment',
          description:
            'A fetal heartbeat may be visible during a follow-up ultrasound examination.'
        },
        {
          startDay: 150,
          endDay: 150,
          title: 'Review the pregnancy health plan',
          description:
            'Discuss vaccination, parasite control, nutrition and monitoring with your veterinarian.'
        },
        {
          startDay: 310,
          endDay: 310,
          title: 'Move to the foaling environment',
          description:
            'Allow the mare to settle and begin close observation for udder development, waxing and behavioural changes.'
        }
      ]
    },

    donkey: {
      name: 'Donkey',
      eventName: 'Foaling',
      eventNameLower: 'foaling',
      offspringSingular: 'foal',
      offspringPlural: 'foals',
      gestationDays: 365,
      methods: [
        {
          value: 'covering',
          label: 'Last covering date',
          estimatedDays: 365,
          minimumDays: 340,
          maximumDays: 385,
          conceptionOffset: 0,
          hint:
            'Donkey gestation is often around 12 months and may vary considerably between individuals.'
        }
      ],
      milestones: [
        {
          startDay: 14,
          endDay: 16,
          title: 'Ultrasound confirmation',
          description:
            'Confirm pregnancy and check for twins.'
        },
        {
          startDay: 25,
          endDay: 30,
          title: 'Fetal heartbeat assessment',
          description:
            'A fetal heartbeat may be visible during follow-up ultrasound.'
        },
        {
          startDay: 330,
          endDay: 330,
          title: 'Prepare the foaling area',
          description:
            'Move the jenny to a quiet, familiar area and monitor for udder development and behavioural changes.'
        }
      ]
    },

    cow: {
      name: 'Cow',
      eventName: 'Calving',
      eventNameLower: 'calving',
      offspringSingular: 'calf',
      offspringPlural: 'calves',
      gestationDays: 283,
      methods: [
        {
          value: 'breeding',
          label: 'Breeding / insemination date',
          estimatedDays: 283,
          minimumDays: 279,
          maximumDays: 287,
          conceptionOffset: 0,
          hint:
            'Cattle gestation averages approximately 283 days. Breed, calf sex and individual variation can shift the date.'
        }
      ],
      milestones: [
        {
          startDay: 30,
          endDay: 45,
          title: 'Pregnancy diagnosis',
          description:
            'Pregnancy may be confirmed by ultrasound, palpation or an appropriate laboratory test.'
        },
        {
          startDay: 223,
          endDay: 223,
          title: 'Dry-off planning for dairy cows',
          description:
            'A dry period of approximately 60 days is commonly planned before calving.'
        },
        {
          startDay: 275,
          endDay: 275,
          title: 'Move to the calving area',
          description:
            'Prepare a clean area and monitor udder development, vulval relaxation and pelvic-ligament changes.'
        }
      ]
    },

    sheep: {
      name: 'Sheep',
      eventName: 'Lambing',
      eventNameLower: 'lambing',
      offspringSingular: 'lamb',
      offspringPlural: 'lambs',
      gestationDays: 147,
      methods: [
        {
          value: 'mating',
          label: 'Mating / raddle-mark date',
          estimatedDays: 147,
          minimumDays: 144,
          maximumDays: 152,
          conceptionOffset: 0,
          hint:
            'Ewe gestation averages approximately 147 days, with normal variation between breeds, individuals and litter sizes.'
        }
      ],
      milestones: [
        {
          startDay: 45,
          endDay: 90,
          title: 'Pregnancy scanning',
          description:
            'Ultrasound can confirm pregnancy and estimate lamb numbers to support nutritional planning.'
        },
        {
          startDay: 100,
          endDay: 100,
          title: 'Increase nutritional monitoring',
          description:
            'Most fetal growth occurs during the final six weeks, increasing energy and protein requirements.'
        },
        {
          startDay: 140,
          endDay: 140,
          title: 'Prepare the lambing area',
          description:
            'Move ewes to clean, sheltered accommodation and begin closer observation.'
        }
      ]
    },

    goat: {
      name: 'Goat',
      eventName: 'Kidding',
      eventNameLower: 'kidding',
      offspringSingular: 'kid',
      offspringPlural: 'kids',
      gestationDays: 150,
      methods: [
        {
          value: 'breeding',
          label: 'Breeding date',
          estimatedDays: 150,
          minimumDays: 145,
          maximumDays: 155,
          conceptionOffset: 0,
          hint:
            'Doe gestation averages approximately 150 days, with a common range of around 145–155 days.'
        }
      ],
      milestones: [
        {
          startDay: 30,
          endDay: 45,
          title: 'Ultrasound confirmation',
          description:
            'Confirm pregnancy and, where possible, estimate the number of kids.'
        },
        {
          startDay: 100,
          endDay: 100,
          title: 'Review final-trimester care',
          description:
            'Adjust nutrition appropriately and discuss pre-kidding vaccination with your veterinarian.'
        },
        {
          startDay: 145,
          endDay: 145,
          title: 'Prepare the kidding pen',
          description:
            'Watch for udder filling, nesting behaviour and softening around the tail head.'
        }
      ]
    },

    pig: {
      name: 'Pig',
      eventName: 'Farrowing',
      eventNameLower: 'farrowing',
      offspringSingular: 'piglet',
      offspringPlural: 'piglets',
      gestationDays: 114,
      methods: [
        {
          value: 'breeding',
          label: 'Mating / insemination date',
          estimatedDays: 114,
          minimumDays: 111,
          maximumDays: 117,
          conceptionOffset: 0,
          hint:
            'Pig gestation averages approximately 114 days — often remembered as three months, three weeks and three days.'
        }
      ],
      milestones: [
        {
          startDay: 21,
          endDay: 30,
          title: 'Pregnancy confirmation',
          description:
            'Ultrasound can often confirm pregnancy from approximately three to four weeks.'
        },
        {
          startDay: 90,
          endDay: 90,
          title: 'Review late-pregnancy feeding',
          description:
            'Adjust feed appropriately for final fetal growth while avoiding excessive body condition.'
        },
        {
          startDay: 110,
          endDay: 110,
          title: 'Move to the farrowing accommodation',
          description:
            'Prepare a clean environment and watch for restlessness, nest-building and udder changes.'
        }
      ]
    },

    alpaca: {
      name: 'Alpaca',
      eventName: 'Birthing',
      eventNameLower: 'birthing',
      offspringSingular: 'cria',
      offspringPlural: 'crias',
      gestationDays: 340,
      methods: [
        {
          value: 'mating',
          label: 'Mating date',
          estimatedDays: 340,
          minimumDays: 335,
          maximumDays: 350,
          conceptionOffset: 0,
          hint:
            'Alpaca gestation averages approximately 340 days, although individual pregnancies vary.'
        }
      ],
      milestones: [
        {
          startDay: 14,
          endDay: 21,
          title: 'Pregnancy assessment',
          description:
            'A spit-off response may suggest pregnancy, but ultrasound provides more reliable confirmation.'
        },
        {
          startDay: 30,
          endDay: 30,
          title: 'Ultrasound confirmation',
          description:
            'Confirm an ongoing pregnancy with your veterinarian.'
        },
        {
          startDay: 300,
          endDay: 300,
          title: 'Prepare the cria area',
          description:
            'Provide a clean, dry and sheltered area. Many camelids give birth during daylight hours.'
        }
      ]
    },

    llama: {
      name: 'Llama',
      eventName: 'Birthing',
      eventNameLower: 'birthing',
      offspringSingular: 'cria',
      offspringPlural: 'crias',
      gestationDays: 350,
      methods: [
        {
          value: 'mating',
          label: 'Mating date',
          estimatedDays: 350,
          minimumDays: 330,
          maximumDays: 370,
          conceptionOffset: 0,
          hint:
            'Llama gestation averages approximately 350 days, with substantial normal variation between individuals.'
        }
      ],
      milestones: [
        {
          startDay: 14,
          endDay: 21,
          title: 'Pregnancy assessment',
          description:
            'A spit-off response may suggest pregnancy, but ultrasound provides more reliable confirmation.'
        },
        {
          startDay: 30,
          endDay: 30,
          title: 'Ultrasound confirmation',
          description:
            'Confirm an ongoing pregnancy with your veterinarian.'
        },
        {
          startDay: 310,
          endDay: 310,
          title: 'Prepare the cria area',
          description:
            'Provide a clean, dry and sheltered birthing area and begin closer observation.'
        }
      ]
    }
  };

  class SAnimalDueDateCalculator extends HTMLElement {
    connectedCallback() {
      this.abortController?.abort();

      this.abortController = new AbortController();
      this.signal = this.abortController.signal;

      this.state = {
        species: this.getInitialSpecies(),
        method: '',
        calculated: false
      };

      this.cacheElements();

      if (!this.hasRequiredElements()) {
        console.warn(
          'Animal Due Date Calculator could not initialise because required markup is missing.',
          this
        );

        return;
      }

      this.bindEvents();
      this.initialise();
    }

    disconnectedCallback() {
      this.abortController?.abort();
      this.abortController = null;
    }

    cacheElements() {
      this.form = this.querySelector('[data-addc-form]');
      this.speciesSelect = this.querySelector('[data-addc-species]');
      this.referenceDateInput = this.querySelector(
        '[data-addc-reference-date]'
      );

      this.methodSelect = this.querySelector('[data-addc-method]');
      this.methodHint = this.querySelector(
        '[data-addc-method-hint]'
      );

      this.errorsWrap = this.querySelector('[data-addc-error]');
      this.results = this.querySelector('[data-addc-result]');

      this.resultSummary = this.querySelector(
        '[data-addc-result-summary]'
      );

      this.dueLabel = this.querySelector('[data-addc-due-label]');
      this.dueDate = this.querySelector('[data-addc-due-date]');
      this.dueRange = this.querySelector('[data-addc-due-range]');

      this.stageNumber = this.querySelector(
        '[data-addc-stage-number]'
      );

      this.stageCaption = this.querySelector(
        '[data-addc-stage-caption]'
      );

      this.progressText = this.querySelector(
        '[data-addc-progress-text]'
      );

      this.progressFill = this.querySelector(
        '[data-addc-progress-fill]'
      );

      this.progressTrack =
        this.progressFill?.closest('[role="progressbar"]') || null;

      this.termEnd = this.querySelector('[data-addc-term-end]');
      this.timeline = this.querySelector('[data-addc-timeline]');

      this.emergencyTitle = this.querySelector(
        '[data-addc-emergency-title]'
      );

      this.emergencyList = this.querySelector(
        '[data-addc-emergency-list]'
      );

      this.notes = this.querySelector('[data-addc-notes]');
      this.primaryCta = this.querySelector(
        '[data-addc-primary-cta]'
      );

      this.restartButton = this.querySelector(
        '[data-addc-restart]'
      );
    }

    hasRequiredElements() {
      return Boolean(
        this.form &&
          this.speciesSelect &&
          this.referenceDateInput &&
          this.methodSelect &&
          this.results &&
          this.dueDate &&
          this.timeline
      );
    }

    bindEvents() {
      if (!this.signal) {
        return;
      }

      this.form.addEventListener(
        'submit',
        (event) => this.onSubmit(event),
        {
          signal: this.signal
        }
      );

      this.speciesSelect.addEventListener(
        'change',
        (event) => this.onSpeciesChange(event),
        {
          signal: this.signal
        }
      );

      this.methodSelect.addEventListener(
        'change',
        (event) => this.onMethodChange(event),
        {
          signal: this.signal
        }
      );

      this.referenceDateInput.addEventListener(
        'change',
        () => this.onReferenceDateChange(),
        {
          signal: this.signal
        }
      );

      this.referenceDateInput.addEventListener(
        'input',
        () => this.clearErrors(),
        {
          signal: this.signal
        }
      );

      this.restartButton?.addEventListener(
        'click',
        () => this.restart(),
        {
          signal: this.signal
        }
      );
    }

    initialise() {
      this.setSpecies(this.state.species);
      this.populateMethods();
      this.setDefaultReferenceDate();
      this.applyStaticLinks();
      this.updateMethodHint();
      this.clearErrors();
      this.hideResults();
    }

    getInitialSpecies() {
      const configuredSpecies = String(
        this.dataset.defaultSpecies || ''
      )
        .trim()
        .toLowerCase();

      if (SPECIES_DATA[configuredSpecies]) {
        return configuredSpecies;
      }

      const selectedSpecies = String(
        this.querySelector('[data-addc-species]')?.value || ''
      )
        .trim()
        .toLowerCase();

      return SPECIES_DATA[selectedSpecies]
        ? selectedSpecies
        : 'dog';
    }

    setSpecies(species) {
      const safeSpecies = SPECIES_DATA[species]
        ? species
        : 'dog';

      this.state.species = safeSpecies;
      this.speciesSelect.value = safeSpecies;
    }

    setDefaultReferenceDate() {
      if (this.referenceDateInput.value) {
        return;
      }

      this.referenceDateInput.value = this.toInputDate(
        this.today()
      );
    }

    applyStaticLinks() {
      const primaryCtaLink = String(
        this.dataset.primaryCtaLink || ''
      ).trim();

      if (primaryCtaLink && this.primaryCta) {
        this.primaryCta.href = primaryCtaLink;
      }
    }

    onSpeciesChange(event) {
      const species = String(event.target?.value || '')
        .trim()
        .toLowerCase();

      if (!SPECIES_DATA[species]) {
        return;
      }

      this.state.species = species;
      this.populateMethods();

      if (this.state.calculated) {
        this.calculate({
          shouldScroll: false,
          showErrors: false
        });
      }
    }

    onMethodChange(event) {
      this.state.method = String(event.target?.value || '');
      this.updateMethodHint();

      if (this.state.calculated) {
        this.calculate({
          shouldScroll: false,
          showErrors: false
        });
      }
    }

    onReferenceDateChange() {
      this.clearErrors();

      if (this.state.calculated) {
        this.calculate({
          shouldScroll: false,
          showErrors: false
        });
      }
    }

    onSubmit(event) {
      event.preventDefault();

      this.calculate({
        shouldScroll: true,
        showErrors: true
      });
    }

    populateMethods() {
      const species = this.getSpeciesData();
      const previousMethod = this.state.method;

      const fragment = document.createDocumentFragment();

      species.methods.forEach((method) => {
        const option = document.createElement('option');

        option.value = method.value;
        option.textContent = method.label;

        fragment.appendChild(option);
      });

      this.methodSelect.replaceChildren(fragment);

      const previousMethodIsValid = species.methods.some(
        (method) => method.value === previousMethod
      );

      const nextMethod = previousMethodIsValid
        ? previousMethod
        : species.methods[0].value;

      this.methodSelect.value = nextMethod;
      this.state.method = nextMethod;
      this.methodSelect.disabled = species.methods.length === 1;

      this.updateMethodHint();
    }

    updateMethodHint() {
      if (!this.methodHint) {
        return;
      }

      const method = this.getMethodData();

      this.methodHint.textContent = method?.hint || '';
    }

    calculate({
      shouldScroll = false,
      showErrors = true
    } = {}) {
      this.clearErrors();

      const parsed = this.parseInputs();

      if (!parsed.ok) {
        if (showErrors) {
          this.renderErrors(parsed.errors);
        }

        return;
      }

      const result = this.calculateResult(parsed.data);

      this.renderResult(result);
      this.showResults();

      if (shouldScroll) {
        requestAnimationFrame(() => {
          this.results.scrollIntoView({
            behavior: this.prefersReducedMotion()
              ? 'auto'
              : 'smooth',
            block: 'start'
          });
        });
      }

      this.dispatchEvent(
        new CustomEvent('animal-due-date:calculated', {
          bubbles: true,
          detail: {
            species: result.speciesKey,
            method: result.method.value,
            referenceDate: this.toInputDate(
              result.referenceDate
            ),
            estimatedDueDate: this.toInputDate(
              result.estimatedDueDate
            ),
            earliestDueDate: this.toInputDate(
              result.earliestDueDate
            ),
            latestDueDate: this.toInputDate(
              result.latestDueDate
            ),
            daysAlong: result.daysAlong,
            progressPercentage: result.progressPercentage
          }
        })
      );
    }

    parseInputs() {
      const errors = [];
      const speciesKey = String(
        this.speciesSelect.value || ''
      );

      const species = SPECIES_DATA[speciesKey];
      const methodValue = String(
        this.methodSelect.value || ''
      );

      const referenceDate = this.parseInputDate(
        this.referenceDateInput.value
      );

      if (!species) {
        errors.push('Choose an animal species.');
      }

      const method = species?.methods.find(
        (item) => item.value === methodValue
      );

      if (!method) {
        errors.push('Choose how the reference date was measured.');
      }

      if (!referenceDate) {
        errors.push(
          this.errorsWrap?.dataset.invalidDateMessage ||
            'Enter a valid reference date before calculating.'
        );
      }

      if (errors.length) {
        return {
          ok: false,
          errors
        };
      }

      return {
        ok: true,
        data: {
          speciesKey,
          species,
          method,
          referenceDate
        }
      };
    }

    calculateResult(data) {
      const estimatedDueDate = this.addDays(
        data.referenceDate,
        data.method.estimatedDays
      );

      const earliestDueDate = this.addDays(
        data.referenceDate,
        data.method.minimumDays
      );

      const latestDueDate = this.addDays(
        data.referenceDate,
        data.method.maximumDays
      );

      /*
       * Milestones and progress are shown from the estimated conception
       * point. For an LH-surge date this is approximately two days later.
       * For cytological diestrus the estimated conception point precedes
       * the entered reference date.
       */
      const estimatedConceptionDate = this.addDays(
        data.referenceDate,
        data.method.conceptionOffset
      );

      const today = this.today();

      const daysAlong = this.differenceInCalendarDays(
        today,
        estimatedConceptionDate
      );

      const progressPercentage = this.clamp(
        Math.round(
          (daysAlong / data.species.gestationDays) * 100
        ),
        0,
        100
      );

      return {
        ...data,
        estimatedDueDate,
        earliestDueDate,
        latestDueDate,
        estimatedConceptionDate,
        daysAlong,
        progressPercentage,
        isBeforeConception: daysAlong < 0,
        isPastExpectedTerm:
          daysAlong > data.method.maximumDays
      };
    }

    renderResult(result) {
      this.renderSummary(result);
      this.renderDueDate(result);
      this.renderStage(result);
      this.renderProgress(result);
      this.renderTimeline(result);
      this.renderEmergencyGuidance(result);
      this.renderNotes(result);
    }

    renderSummary(result) {
      if (!this.resultSummary) {
        return;
      }

      this.resultSummary.textContent =
        `${result.species.name} · ` +
        `${result.method.label} · ` +
        `${this.formatFullDate(result.referenceDate)}`;
    }

    renderDueDate(result) {
      if (this.dueLabel) {
        this.dueLabel.textContent =
          `Estimated ${result.species.eventNameLower} date`;
      }

      this.dueDate.textContent = this.formatFullDate(
        result.estimatedDueDate
      );

      if (this.dueRange) {
        this.dueRange.textContent =
          `Likely window: ` +
          `${this.formatShortDate(result.earliestDueDate)} – ` +
          `${this.formatFullDate(result.latestDueDate)} ` +
          `(${result.method.minimumDays}–` +
          `${result.method.maximumDays} days after the reference date)`;
      }
    }

    renderStage(result) {
      if (!this.stageNumber || !this.stageCaption) {
        return;
      }

      if (result.isBeforeConception) {
        this.stageNumber.textContent = String(
          Math.abs(result.daysAlong)
        );

        this.stageCaption.textContent =
          Math.abs(result.daysAlong) === 1
            ? 'day until estimated conception'
            : 'days until estimated conception';

        return;
      }

      this.stageNumber.textContent = String(
        result.daysAlong
      );

      if (result.isPastExpectedTerm) {
        this.stageCaption.textContent =
          'days along — beyond the expected window';

        return;
      }

      this.stageCaption.textContent =
        `days along · about ${result.species.gestationDays} days total`;
    }

    renderProgress(result) {
      let progressText =
        `${result.progressPercentage}% through`;

      if (result.isBeforeConception) {
        progressText = 'Not started';
      } else if (result.isPastExpectedTerm) {
        progressText = 'Beyond expected term';
      } else if (
        result.daysAlong >= result.species.gestationDays
      ) {
        progressText = 'At expected term';
      }

      if (this.progressText) {
        this.progressText.textContent = progressText;
      }

      if (this.progressFill) {
        this.progressFill.style.width =
          `${result.progressPercentage}%`;

        this.progressFill.setAttribute(
          'aria-valuenow',
          String(result.progressPercentage)
        );
      }

      if (this.progressTrack) {
        this.progressTrack.setAttribute(
          'aria-valuenow',
          String(result.progressPercentage)
        );

        this.progressTrack.setAttribute(
          'aria-valuetext',
          progressText
        );
      }

      if (this.termEnd) {
        this.termEnd.textContent =
          `${result.species.eventName} ` +
          `(~${result.species.gestationDays} days)`;
      }
    }

    renderTimeline(result) {
      const milestones = [
        ...result.species.milestones,
        {
          startDay: result.species.gestationDays,
          endDay: result.species.gestationDays,
          title: `Estimated ${result.species.eventNameLower}`,
          description:
            'Expect birth around this time. Monitor closely and review the urgent warning signs below.'
        }
      ];

      const fragment = document.createDocumentFragment();

      milestones.forEach((milestone) => {
        const startDate = this.addDays(
          result.estimatedConceptionDate,
          milestone.startDay
        );

        const endDate = this.addDays(
          result.estimatedConceptionDate,
          milestone.endDay
        );

        const isSingleDay =
          milestone.startDay === milestone.endDay;

        const isComplete =
          result.daysAlong > milestone.endDay;

        const item = document.createElement('li');

        item.className =
          'animal-due-date-calculator__timeline-item';

        if (isComplete) {
          item.classList.add('is-complete');
        }

        const dayColumn = document.createElement('div');

        dayColumn.className =
          'animal-due-date-calculator__timeline-day';

        const dayText = document.createElement('span');

        dayText.textContent = isSingleDay
          ? `Day ${milestone.startDay}`
          : `Days ${milestone.startDay}–${milestone.endDay}`;

        dayColumn.appendChild(dayText);

        if (isComplete) {
          const completedLabel =
            document.createElement('small');

          completedLabel.textContent = 'Passed';
          dayColumn.appendChild(completedLabel);
        }

        const content = document.createElement('div');

        const title = document.createElement('h3');

        title.className =
          'animal-due-date-calculator__timeline-title';

        title.textContent = milestone.title;

        const date = document.createElement('div');

        date.className =
          'animal-due-date-calculator__timeline-date';

        date.textContent = isSingleDay
          ? this.formatFullDate(startDate)
          : `${this.formatShortDate(startDate)} – ` +
            `${this.formatFullDate(endDate)}`;

        const description = document.createElement('p');

        description.className =
          'animal-due-date-calculator__timeline-description';

        description.textContent = milestone.description;

        content.append(title, date, description);
        item.append(dayColumn, content);
        fragment.appendChild(item);
      });

      this.timeline.replaceChildren(fragment);
    }

    renderEmergencyGuidance(result) {
      if (this.emergencyTitle) {
        this.emergencyTitle.textContent =
          `Call a vet urgently during ` +
          `${result.species.eventNameLower} if you see:`;
      }

      if (!this.emergencyList) {
        return;
      }

      const warnings = this.getEmergencyWarnings(result);
      const fragment = document.createDocumentFragment();

      warnings.forEach((warning) => {
        const item = document.createElement('li');

        item.textContent = warning;
        fragment.appendChild(item);
      });

      this.emergencyList.replaceChildren(fragment);
    }

    getEmergencyWarnings(result) {
      const species = result.species;

      return [
        `Strong, active straining or contractions with no ${species.offspringSingular} produced and no progress.`,
        `A prolonged gap between ${species.offspringPlural} when more are known or strongly suspected to remain.`,
        `Green, black, bloody or foul-smelling discharge before the first ${species.offspringSingular} is born, unless your veterinarian has advised that it is expected for this species.`,
        `A ${species.offspringSingular} visibly stuck in the birth canal without progress.`,
        `The pregnancy has passed ${this.formatFullDate(result.latestDueDate)} with no signs of ${species.eventNameLower}.`,
        'The mother is weak, collapsed, very painful, unable to stand, refusing food or clearly unwell.'
      ];
    }

    renderNotes(result) {
      if (!this.notes) {
        return;
      }

      const gestationLabel =
        document.createElement('strong');

      gestationLabel.textContent =
        `Typical gestation for a ${result.species.name.toLowerCase()}: `;

      const gestationText = document.createTextNode(
        `about ${result.species.gestationDays} days. `
      );

      const methodLabel =
        document.createElement('strong');

      methodLabel.textContent =
        'Why the selected method matters: ';

      const methodText = document.createTextNode(
        `${result.method.hint} Individual animals vary, so use the calculated window as a guide and confirm pregnancy progress with your veterinarian.`
      );

      this.notes.replaceChildren(
        gestationLabel,
        gestationText,
        methodLabel,
        methodText
      );
    }

    renderErrors(errors) {
      if (!this.errorsWrap) {
        return;
      }

      const fragment = document.createDocumentFragment();

      errors.forEach((error) => {
        const item = document.createElement('div');

        item.textContent = error;
        fragment.appendChild(item);
      });

      this.errorsWrap.replaceChildren(fragment);
      this.errorsWrap.hidden = false;
      this.errorsWrap.setAttribute(
        'aria-hidden',
        'false'
      );

      this.referenceDateInput.setAttribute(
        'aria-invalid',
        'true'
      );

      this.errorsWrap.scrollIntoView({
        behavior: this.prefersReducedMotion()
          ? 'auto'
          : 'smooth',
        block: 'nearest'
      });
    }

    clearErrors() {
      if (!this.errorsWrap) {
        return;
      }

      this.errorsWrap.replaceChildren();
      this.errorsWrap.hidden = true;
      this.errorsWrap.setAttribute(
        'aria-hidden',
        'true'
      );

      this.referenceDateInput?.removeAttribute(
        'aria-invalid'
      );
    }

    showResults() {
      this.results.hidden = false;
      this.results.setAttribute('aria-hidden', 'false');
      this.state.calculated = true;
    }

    hideResults() {
      this.results.hidden = true;
      this.results.setAttribute('aria-hidden', 'true');
      this.state.calculated = false;
    }

    restart() {
      this.hideResults();
      this.clearErrors();

      requestAnimationFrame(() => {
        this.form.scrollIntoView({
          behavior: this.prefersReducedMotion()
            ? 'auto'
            : 'smooth',
          block: 'start'
        });

        this.referenceDateInput.focus({
          preventScroll: true
        });
      });
    }

    getSpeciesData() {
      return (
        SPECIES_DATA[this.state.species] ||
        SPECIES_DATA.dog
      );
    }

    getMethodData() {
      const species = this.getSpeciesData();

      return (
        species.methods.find(
          (method) =>
            method.value === this.state.method
        ) || species.methods[0]
      );
    }

    parseInputDate(value) {
      const normalizedValue = String(value || '').trim();

      if (!/^\d{4}-\d{2}-\d{2}$/.test(normalizedValue)) {
        return null;
      }

      const [year, month, day] = normalizedValue
        .split('-')
        .map(Number);

      const date = new Date(year, month - 1, day);

      if (
        date.getFullYear() !== year ||
        date.getMonth() !== month - 1 ||
        date.getDate() !== day
      ) {
        return null;
      }

      return date;
    }

    addDays(date, numberOfDays) {
      return new Date(
        date.getFullYear(),
        date.getMonth(),
        date.getDate() + Number(numberOfDays)
      );
    }

    differenceInCalendarDays(laterDate, earlierDate) {
      const laterUtc = Date.UTC(
        laterDate.getFullYear(),
        laterDate.getMonth(),
        laterDate.getDate()
      );

      const earlierUtc = Date.UTC(
        earlierDate.getFullYear(),
        earlierDate.getMonth(),
        earlierDate.getDate()
      );

      return Math.round(
        (laterUtc - earlierUtc) / DAY_IN_MS
      );
    }

    today() {
      const now = new Date();

      return new Date(
        now.getFullYear(),
        now.getMonth(),
        now.getDate()
      );
    }

    toInputDate(date) {
      const year = date.getFullYear();

      const month = String(
        date.getMonth() + 1
      ).padStart(2, '0');

      const day = String(
        date.getDate()
      ).padStart(2, '0');

      return `${year}-${month}-${day}`;
    }

    formatFullDate(date) {
      return new Intl.DateTimeFormat(
        this.getLocale(),
        {
          weekday: 'short',
          day: 'numeric',
          month: 'short',
          year: 'numeric'
        }
      ).format(date);
    }

    formatShortDate(date) {
      return new Intl.DateTimeFormat(
        this.getLocale(),
        {
          day: 'numeric',
          month: 'short'
        }
      ).format(date);
    }

    getLocale() {
      return (
        String(this.dataset.locale || '').trim() ||
        document.documentElement.lang ||
        'en-GB'
      );
    }

    clamp(value, minimum, maximum) {
      return Math.min(
        Math.max(value, minimum),
        maximum
      );
    }

    prefersReducedMotion() {
      return window.matchMedia(
        '(prefers-reduced-motion: reduce)'
      ).matches;
    }
  }

  if (
    !customElements.get(
      's-animal-due-date-calculator'
    )
  ) {
    customElements.define(
      's-animal-due-date-calculator',
      SAnimalDueDateCalculator
    );
  }
})();
